/**
 *  Utility functions for application configuration.
 * @module app
 */

import { AppConfig } from "../types";

/**
 * Retrieves the application configuration from Vite environment variables.
 *
 *  getConfig
 * @returns {AppConfig} Configuration object with app name, environment, and version
 * @module app
 * @category Configuration
 *
 * @remarks
 * This function reads configuration from Vite's `import.meta.env` object.
 * The following environment variables are used:
 *
 * | Variable | Description |
 * |----------|-------------|
 * | `VITE_APP_NAME` | Application display name |
 * | `MODE` | Current Vite mode (development, production) |
 * | `VITE_APP_RELEASE` | Semantic version of the release |
 * | `VITE_APP_BRANCH` | Git branch name |
 *
 * Environment variables are set in `.env` files or via the build process.
 *
 * @example
 * ```typescript
 * import { getConfig } from './utils';
 *
 * const config = getConfig();
 * console.log(config.name);    // 'LOA Frontend'
 * console.log(config.env);     // 'development'
 * console.log(config.version); // { release: '1.0.0', branch: 'main' }
 * ```
 *
 * @see {@link AppConfig} for the configuration interface
 * @see {@link Application} for how the config is used
 */
export function getConfig(): AppConfig {
  return {
    name: import.meta.env.VITE_APP_NAME,
    env: import.meta.env.MODE,
    version: {
      release: import.meta.env.VITE_APP_RELEASE,
      branch: import.meta.env.VITE_APP_BRANCH,
    },
  };
}

/**
 * Check if the application is running in production mode.
 *
 * @returns {boolean} True if running in production, false otherwise
 * @module app
 * @category Configuration
 *
 * @remarks
 * Uses Vite's `import.meta.env.PROD` which is `true` when built with
 * `vite build` (mode=production) and `false` in development.
 *
 * @example
 * ```typescript
 * import { isProd } from './utils';
 *
 * if (isProd()) {
 *   // Enable production-only features
 * }
 * ```
 */
export function isProd(): boolean {
  return import.meta.env.PROD;
}

/**
 * Get the application environment identifier.
 *
 * @returns {string} Environment identifier (qa, production, development)
 * @module app
 * @category Configuration
 *
 * @remarks
 * Uses VITE_APP_ENV environment variable set in .env files.
 * Defaults to 'development' if not set.
 *
 * @example
 * ```typescript
 * import { getEnvironment } from './utils';
 *
 * const env = getEnvironment();
 * // In QA: 'qa'
 * // In production: 'production'
 * // In local dev: 'development'
 * ```
 */
export function getEnvironment(): string {
  return import.meta.env.VITE_APP_ENV ?? 'development';
}
